import React, {FunctionComponent, useCallback, useEffect, useState,} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Colors, Icon, Spinner} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {shareWithGroup} from '../../../store/actions/sharingSettingsActions';
import {loadRoles} from '../../../store/actions/roleActions';
import {IRole, ResourceSharingSettings} from '../../../models';
import {IState} from '../../../store/reducers';
import {useCurrentUser} from '../../../hooks';
import SelectAction from './SelectAction';
import SuggestUser from './SuggestUser';
import {getPermission} from './utils';
import {ENTITY_TYPE} from './constants';

type Callback = (value?: any) => void;
type UserActionCallback = (uid: string, action: string) => void;

interface WithGroupContainerProps {
  uid: string;
  sharingSettings: ResourceSharingSettings;
  type: ENTITY_TYPE;
  onShare?: Callback;
}

interface Option {
  value: string;
  uid: string;
}

interface WithGroupInputProps {
  options: Option[];
  onSelect: Callback;
}

interface WithGroupListProps {
  groups: IRole[];
  selected: string[]; /* user uid */
  onChange: UserActionCallback;
  onRemove: Callback;
}

const WithGroupInput: FunctionComponent<WithGroupInputProps> = ({options, onSelect}: WithGroupInputProps) => {
  return (
    <div>
      <div className="sharing-settings-dialog__with-user__label">
        <Icon icon={IconNames.PEOPLE} color={Colors.GRAY1}/> Share with group:
      </div>
      <SuggestUser
        options={options}
        onSelect={onSelect}
        placeholder="Search for group"
      />
    </div>
  );
}

const WithGroupList: FunctionComponent<WithGroupListProps> = ({
  groups,
  selected,
  onChange,
  onRemove
}: WithGroupListProps) => {
  const selectedGroups = groups.filter((user: IRole) => selected.find((s: any) => s.uid === user.uid));

  return (
    <div className="sharing-settings-dialog__with-user__share-list">
      {
        selectedGroups.map((group: IRole) => {
          const name = group.name;
          const action = getPermission(selected.find((s: any) => s.uid === group.uid));

          return (
            <div
              className="sharing-settings-dialog__with-user__share-list__item "
              key={group.uid}>
              <div className="user-details">
                <div className="user-name">{name}</div>
              </div>
              <div className="remove-user">
                <SelectAction
                  key={group.uid}
                  selected={action}
                  onChange={(option: Option) => onChange(group.uid, option.value)}
                  disabled={false}
                />
                <Button
                  icon={IconNames.CROSS}
                  minimal={true}
                  onClick={() => onRemove(group.uid)}
                />
              </div>
            </div>
          );
        })
      }
    </div>
  );
}

const WithGroupContainer: FunctionComponent<WithGroupContainerProps> = ({
  uid: entityUid,
  sharingSettings: currentSharingSettings,
  type,
  onShare,
}: WithGroupContainerProps) => {
  /*
   * A "Group" represents a user defined role.
   */
  const [init, setInit] = useState(false);
  const [selected, setSelected] = useState([] as any[]);
  const groups = useSelector((state: IState) => state.roles.groups);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  useEffect(() => {
    if (init) {
      return;
    }
    if (!groups.length) {
      dispatch(loadRoles());
    }
    setInit(true);
  }, [groups, init]);

  useEffect(() => {
    if (
      currentSharingSettings &&
      currentSharingSettings.shared_with_groups &&
      currentSharingSettings.shared_with_groups.length) {
      setSelected(currentSharingSettings.shared_with_groups);
    }
  }, [currentSharingSettings]);

  const onSelect = useCallback((value: Option) => {
    const found = selected.find((s: any) => s.uid === value.uid);

    if (!found && entityUid) { /* if not found */
      onShare && onShare(); // shared with another group
      /*
       * view is selected by default
       */
      const newSelected = {uid: value.uid, view: true};

      setSelected((oldSelected: any) => [...oldSelected, newSelected]);

      dispatch(shareWithGroup(type as any, entityUid, value.uid, getPermission(newSelected)));
    }

  }, [entityUid, selected.length]);

  const onRemoveSelected = useCallback((uid: string) => {
    if (uid) {
      onShare && onShare(); // removed user from sharing settings

      setSelected((oldSelected: any) => [...oldSelected.filter((s: any) => s.uid !== uid)]);

      // if permission is not specified, it will be deleted
      dispatch(shareWithGroup(type as any, entityUid, uid, ""));
    }
  }, [entityUid, selected.length]);

  const onChangeSelected = useCallback((uid: string, action: string) => {
    onShare && onShare(); // changed user sharing settings

    dispatch(shareWithGroup(type as any, entityUid, uid, action));
  }, [entityUid, selected.length]);

  if (!groups.length && !currentSharingSettings) {
    return <Spinner size={20}/>;
  }

  const options: Option[] = groups.map((group: IRole) => {
    return {
      value: group.name,
      uid: group.uid
    }
  }).filter((value: Option) => {
    return !selected.find((s: any) => s.uid === value.uid); /* this means it's already selected */
  });

  if (!groups.length && !currentSharingSettings) {
    return <Spinner size={20}/>;
  }

  return (
    <div className="sharing-settings-dialog__with-user">
      <WithGroupInput
        options={options}
        onSelect={onSelect}
      />
      <WithGroupList
        groups={groups}
        selected={selected}
        onChange={onChangeSelected}
        onRemove={onRemoveSelected}
      />
    </div>
  );
};

export default WithGroupContainer;
